import {
  CalendarIcon,
  EnvelopeIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { Link } from "react-router-dom";
import Timestamp from "../../common/components/timestamp";
import { ClipboardContext } from "../../common/context/clipboard";
import { AccountFragment, AccountType, AgentFragment, Application } from "../../graphql/generated";
import AccountItemActions from "./itemactions";
import AccountProfilePic from "./profilepic";
import PageHeader from "../../common/components/page-header";

const accountType = (accountType?: AccountType) => {
  switch (accountType) {
    case AccountType.Agent:
      return "Agent";
    case AccountType.Draft:
      return "Draft";
    case AccountType.Member:
      return "Member";
    case AccountType.Lender:
      return "Lender";
    default:
      return "Unknown";
  }
};

const AccountTitle: React.FC<{ account?: AccountFragment; primaryAgent?: AgentFragment }> = ({ account, primaryAgent }) => {
  const { sendToClip } = React.useContext(ClipboardContext);

  const isRealtorPlus = primaryAgent?.mls?.application === Application.RealtorPlus;
  const rdcEmail = isRealtorPlus ? primaryAgent?.rdcEmail : null;
  const rdcPhone = isRealtorPlus ? primaryAgent?.rdcPhone : null;
  const displayEmail = rdcEmail || account?.email;
  const displayPhone = rdcPhone || account?.phone;
  const emailOverridden = rdcEmail && rdcEmail !== account?.email;
  const phoneOverridden = rdcPhone && rdcPhone !== account?.phone;

  return (
    <PageHeader>
      <PageHeader.Pic>
        <AccountProfilePic account={account} large />
      </PageHeader.Pic>
      <PageHeader.Title>
        {account?.name.trim() === "" ? "Empty Name" : account?.name}
      </PageHeader.Title>
      <PageHeader.Details>
        <PageHeader.DetailsItem icon={UserIcon}>
          {accountType(account?.accountType)}
        </PageHeader.DetailsItem>
        <PageHeader.DetailsItem icon={CalendarIcon}>
          <Timestamp timestamp={account?.createdAt} format="short" popover />
          /
          <Timestamp timestamp={account?.activeAt} format="short" popover />
        </PageHeader.DetailsItem>
      </PageHeader.Details>
      <PageHeader.Details>
        <PageHeader.DetailsItem icon={EnvelopeIcon}>
          <div
            className="text-blue-900 cursor-pointer"
            onClick={() => sendToClip(displayEmail)}
          >
            {displayEmail}
          </div>
          {emailOverridden && (
            <span className="ml-1 text-xs text-gray-400 line-through">{account?.email}</span>
          )}
          <Link
            to={{
              pathname: "/subetha/recipients",
              search: `?email=${displayEmail}`,
            }}
          >
            {}
            <MagnifyingGlassIcon
              className="flex-shrink-0 mx-1.5 h-5 w-5 text-zenlist-400"
              aria-hidden="true"
            />
          </Link>
        </PageHeader.DetailsItem>
        {displayPhone && (
          <PageHeader.DetailsItem icon={PhoneIcon}>
            <a href={`tel:${displayPhone}`}>{displayPhone}</a>
            {phoneOverridden && (
              <span className="ml-1 text-xs text-gray-400 line-through">{account?.phone}</span>
            )}
          </PageHeader.DetailsItem>
        )}
      </PageHeader.Details>

      <PageHeader.Actions>
        <AccountItemActions account={account} />
      </PageHeader.Actions>
    </PageHeader>
  );
};

export default AccountTitle;
